import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./icons/Logo";
import NavPlusIcon from "./icons/NavPlusIcon";
import NewButton from "./NewButton";
import MobileNav from "./header/MobileNav";
import ServicesMegaMenu, { curtainTransition } from "./header/ServicesMegaMenu";
import { topNavLinks } from "../data/headerNav";
import { startLenis, stopLenis } from "../lib/lenisBridge";
import {
    backgroundColors,
    borderColors,
    buttonColors,
    buttonFontColors,
    fontColors,
    svgColors,
} from "../mappings";

/** Shared mobile chrome size — logo / hamburger / close */
const MOBILE_ICON = 40;

const PlusMark = ({ open = false }) => (
    <div
        aria-hidden="true"
        style={{
            width: 9,
            height: 9,
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 2,
        }}
    >
        <div
            style={{
                width: 9,
                height: 9,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: open ? "rotate(45deg)" : "rotate(0deg)",
                transition: "transform 0.32s ease",
            }}
        >
            <NavPlusIcon />
        </div>
    </div>
);

/** Hover reveal — label only */
const NavLabel = ({ children, color, hoverColor = "#417B5A" }) => (
    <span className="tw:relative tw:inline-block tw:overflow-hidden tw:h-[1.2em] tw:leading-[1.2]">
        <span className="tw:block tw:transition-transform tw:duration-500 tw:ease-[cubic-bezier(0.65,0,0.35,1)] tw:group-hover:-translate-y-[1.2em] tw:group-focus-visible:-translate-y-[1.2em]">
            <span className="tw:block tw:h-[1.2em]" style={{ color }}>{children}</span>
            <span className="tw:block tw:h-[1.2em]" style={{ color: hoverColor }} aria-hidden="true">
                {children}
            </span>
        </span>
    </span>
);

const NavItemInner = ({ label, color, plusOpen, showPlus, badge }) => (
    <span className="tw:inline-flex tw:items-start tw:gap-1">
        <NavLabel color={color}>{label}</NavLabel>
        {showPlus ? <PlusMark open={plusOpen} /> : null}
        {badge ? (
            <span className="tw:text-[11px] tw:font-bold tw:leading-none tw:mt-0.5" style={{ color: "#417B5A" }}>
                {badge}
            </span>
        ) : null}
    </span>
);

const navItemClass =
    "tw:group tw:xl:text-lg tw:text-sm tw:font-medium tw:no-underline";

export default function Header() {
    const location = useLocation();
    const path = location.pathname.toLowerCase();
    const rootRef = useRef(null);
    const [openMenu, setOpenMenu] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

    const theme = useMemo(
        () => ({
            bg: backgroundColors[path] || "#ffffff",
            border: borderColors[path] || "#2222221A",
            text: fontColors[path] || "#222",
            icon: svgColors[path] || "#222",
            buttonBg: buttonColors[path] || "#417B5A",
            buttonText: buttonFontColors[path] || "#fff",
        }),
        [path],
    );

    const hideHeaderMenu = path === "/shopify-growth-agency";

    const closeAll = () => {
        setOpenMenu(null);
        setMobileOpen(false);
        setMobileServicesOpen(false);
    };

    useEffect(() => {
        closeAll();
    }, [location.pathname]);

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === "Escape") closeAll();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        const lockedPage =
            location.pathname === "/shopify-growth-agency/get-audit-your-store";

        if (!mobileOpen) {
            if (!lockedPage) startLenis();
            document.body.style.overflow = "";
            return undefined;
        }

        stopLenis();
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
            if (!lockedPage) startLenis();
        };
    }, [mobileOpen, location.pathname]);

    const handleDesktopEnter = (id) => {
        if (id === "services") setOpenMenu(id);
    };

    const desktopMenuOpen = openMenu === "services";

    if (hideHeaderMenu) {
        return (
            <header
                className="tw:relative tw:z-50 tw:border-b"
                style={{ backgroundColor: theme.bg, borderColor: theme.border }}
            >
                <div className="tw:h-[72px] tw:md:h-[88px] tw:xl:h-[100px] tw:flex tw:items-center tw:justify-between tw:px-5 tw:md:px-6 tw:xl:px-10">
                    <Logo />
                    <Link
                        to="/shopify-growth-agency/get-audit-your-store"
                        className="tw:inline-flex tw:items-center tw:justify-center tw:h-11 tw:px-6 tw:rounded-full tw:bg-[#222] tw:text-white tw:text-sm tw:font-medium tw:no-underline"
                    >
                        Get Audit Your Store
                    </Link>
                </div>
            </header>
        );
    }

    return (
        <>
            <AnimatePresence>
                {desktopMenuOpen ? (
                    <motion.div
                        key="mega-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1, transition: curtainTransition }}
                        exit={{ opacity: 0, transition: curtainTransition }}
                        className="tw:fixed tw:inset-0 tw:z-40 tw:hidden tw:lg:block tw:bg-[rgba(232,232,237,0.4)] tw:backdrop-blur-[20px]"
                        onClick={closeAll}
                        aria-hidden="true"
                    />
                ) : null}
            </AnimatePresence>

            <header
                ref={rootRef}
                className={`tw:relative ${mobileOpen ? "tw:z-[70]" : "tw:z-50"}`}
                onMouseLeave={() => setOpenMenu(null)}
            >
                <div
                    className="tw:relative tw:z-50 tw:h-[60px] tw:md:h-[75px] tw:xl:h-[100px] tw:flex tw:items-stretch tw:border-b"
                    style={{ backgroundColor: theme.bg, borderColor: theme.border }}
                >
                    <div className="tw:flex tw:flex-1 tw:items-center tw:min-w-0 tw:pl-5 tw:md:pl-8 tw:1xl:pl-[100px] tw:pr-4 tw:md:pr-2 tw:xl:pr-[18px]">
                        <Link
                            to="/"
                            aria-label="Abox Agency home"
                            title="Abox Agency home"
                            className="tw:shrink-0 tw:inline-flex tw:items-center"
                            onClick={closeAll}
                        >
                            <span className="tw:inline-flex tw:items-center tw:[&_svg]:h-10 tw:md:[&_svg]:h-12 tw:xl:[&_svg]:h-[70px] tw:[&_svg]:w-auto">
                                <Logo />
                            </span>
                            <span className="visually-hidden">Abox Agency home</span>
                        </Link>

                        <div className="tw:hidden tw:lg:flex tw:items-center tw:ml-auto tw:gap-7">
                            <nav className="tw:flex tw:items-center tw:gap-4 tw:xl:gap-7" aria-label="Primary">
                                {topNavLinks.map((item) => {
                                    const isOpen = openMenu === item.id;
                                    const showPlus = Boolean(item.hasMenu || item.hasPlus);

                                    if (item.hasMenu) {
                                        return (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className={`${navItemClass} tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer`}
                                                style={{ color: theme.text }}
                                                aria-expanded={isOpen}
                                                aria-haspopup="true"
                                                onMouseEnter={() => handleDesktopEnter(item.id)}
                                                onFocus={() => handleDesktopEnter(item.id)}
                                                onClick={() => setOpenMenu(isOpen ? null : item.id)}
                                            >
                                                <NavItemInner
                                                    label={item.label}
                                                    color={theme.text}
                                                    plusOpen={isOpen}
                                                    showPlus={showPlus}
                                                    badge={item.badge}
                                                />
                                            </button>
                                        );
                                    }

                                    return (
                                        <Link
                                            key={item.id}
                                            to={item.to}
                                            className={navItemClass}
                                            style={{ color: theme.text }}
                                            onMouseEnter={() => setOpenMenu(null)}
                                        >
                                            <NavItemInner
                                                label={item.label}
                                                color={theme.text}
                                                showPlus={showPlus}
                                                badge={item.badge}
                                            />
                                        </Link>
                                    );
                                })}
                            </nav>

                            <NewButton
                                to="/contact"
                                tone={{ bg: theme.buttonBg, color: theme.buttonText }}
                                className="tw:shrink-0"
                            >
                                Start a Project
                            </NewButton>
                        </div>

                        <button
                            type="button"
                            className="tw:lg:hidden tw:ml-auto tw:inline-flex tw:items-center tw:justify-center tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer"
                            style={{ width: MOBILE_ICON, height: MOBILE_ICON }}
                            aria-label={mobileOpen ? "Close menu" : "Open menu"}
                            aria-expanded={mobileOpen}
                            onClick={() => setMobileOpen((prev) => !prev)}
                        >
                            <span className="tw:flex tw:flex-col tw:justify-center tw:gap-1 tw:w-7">
                                <span
                                    className={`tw:block tw:w-full tw:h-0.5 tw:rounded-full tw:transition-transform tw:duration-300 tw:ease-[cubic-bezier(0.4,0,0.6,1)] tw:origin-center ${mobileOpen ? "tw:translate-y-[3px] tw:rotate-45" : ""
                                        }`}
                                    style={{ backgroundColor: theme.icon }}
                                />
                                <span
                                    className={`tw:block tw:w-full tw:h-0.5 tw:rounded-full tw:transition-transform tw:duration-300 tw:ease-[cubic-bezier(0.4,0,0.6,1)] tw:origin-center ${mobileOpen ? "tw:-translate-y-[3px] tw:-rotate-45" : ""
                                        }`}
                                    style={{ backgroundColor: theme.icon }}
                                />
                            </span>
                        </button>
                    </div>

                    <Link
                        to="/portfolio"
                        className="tw:hidden tw:lg:flex tw:items-center tw:justify-center tw:shrink-0 tw:self-stretch tw:bg-[#AA5042] tw:px-10 tw:xl:px-[70px] tw:no-underline"
                        aria-label="Our portfolio"
                        onMouseEnter={() => setOpenMenu(null)}
                    >
                        <img
                            src="/assets/image/our-portfolio.svg"
                            alt="Our portfolio"
                            width={135}
                            height={43}
                            className="tw:w-[135px] tw:h-[43px] tw:max-w-none tw:block"
                        />
                    </Link>
                </div>

                <div className="tw:hidden tw:lg:block">
                    <ServicesMegaMenu menu={openMenu} onNavigate={closeAll} theme={theme} />
                </div>

            </header>

            <MobileNav
                open={mobileOpen}
                onClose={closeAll}
                servicesOpen={mobileServicesOpen}
                onToggleServices={() => setMobileServicesOpen((prev) => !prev)}
            />
        </>
    );
}